import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  rows: {}, // { [rowId]: RowDraft }
  activeClockRowId: 0,
};

// RowDraft shape:
// {
//   notes: '',
//   mileage: '', mileageUnit: 'km',
//   udFields: [],
//   activityUdFields: [],
//   clockInTime: '00:00', clockOutTime: '00:00', hours: '0:00',
//   pictures: [],
//   localPictures: [],
//   removedPictureIds: []
// }

export const timesheetDraftSlice = createSlice({
  name: 'timesheetDraft',
  initialState,
  reducers: {
    hydrateDraft: (state, action) => {
      const jobs = action.payload;
      
      jobs.forEach(job => {
        const rowId = job.id;
        if (!state.rows[rowId]) {
          const hourList = job.timesheetHours || null;
          const empList = job.timesheetEmployees || null;
          const actList = job.timesheetActivities || null;
          
          let clockIn = hourList?.clockInTime || '00:00';
          let clockOut = hourList?.clockOutTime || '00:00';
          let hours = hourList?.hours || '0:00';
          
          if (clockIn !== '00:00' && clockOut === '00:00') {
             state.activeClockRowId = rowId;
          }
          
          const udFields = (job.udFieldsList || []).map(f => ({...f}));
          const activityUdFields = (actList?.udFieldsList || []).map(f => ({...f}));
          
          state.rows[rowId] = {
            notes: job.notes || '',
            mileage: empList?.mileage || '',
            mileageUnit: empList?.mileageUnit || 'km',
            udFields,
            activityUdFields,
            clockInTime: clockIn,
            clockOutTime: clockOut,
            hours,
            pictures: job.pictureList || [], 
            localPictures: [],
            removedPictureIds: []
          };
        }
      });
    },
    resetDraft: () => initialState,
    setField: (state, action) => {
      const { rowId, field, value } = action.payload;
      if (state.rows[rowId]) {
        state.rows[rowId][field] = value;
      }
    },
    toggleUdField: (state, action) => {
      const { rowId, fieldId, listKey = 'udFields' } = action.payload;
      if (state.rows[rowId]) {
        const field = state.rows[rowId][listKey].find(f => f.id === fieldId || f.udFieldId === fieldId);
        if (field) {
          field.value = field.value === 'true' ? 'false' : 'true';
        }
      }
    },
    setUdFieldValue: (state, action) => {
      const { rowId, fieldId, value, listKey = 'udFields' } = action.payload;
      if (state.rows[rowId]) {
        const field = state.rows[rowId][listKey].find(f => f.id === fieldId || f.udFieldId === fieldId);
        if (field) {
          field.value = value;
        }
      }
    },
    clockIn: (state, action) => {
      const { rowId, time } = action.payload;
      if (state.rows[rowId]) {
        state.rows[rowId].clockInTime = time;
        state.rows[rowId].clockOutTime = '00:00';
        state.activeClockRowId = rowId;
      }
    },
    clockOut: (state, action) => {
      const { rowId, time, hours } = action.payload;
      if (state.rows[rowId]) {
        state.rows[rowId].clockOutTime = time;
        state.rows[rowId].hours = hours;
        if (state.activeClockRowId === rowId) {
          state.activeClockRowId = 0;
        }
      }
    },
    addLocalPictures: (state, action) => {
      const { rowId, pictures } = action.payload;
      if (state.rows[rowId]) {
        state.rows[rowId].localPictures.push(...pictures);
      }
    },
    removeLocalPicture: (state, action) => {
      const { rowId, index } = action.payload;
      if (state.rows[rowId]) {
        state.rows[rowId].localPictures.splice(index, 1);
      }
    },
    removeServerPicture: (state, action) => {
      const { rowId, pictureId } = action.payload;
      if (state.rows[rowId]) {
        state.rows[rowId].removedPictureIds.push(pictureId);
      }
    }
  }
});

export const {
  hydrateDraft,
  resetDraft,
  setField,
  toggleUdField,
  setUdFieldValue,
  clockIn,
  clockOut,
  addLocalPictures,
  removeLocalPicture,
  removeServerPicture
} = timesheetDraftSlice.actions;

// Selectors
export const selectActiveClockRowId = (state) => state.timesheetDraft.activeClockRowId;
export const selectRowField = (state, rowId, field) => state.timesheetDraft.rows[rowId]?.[field];
export const selectRowUdFields = (state, rowId) => state.timesheetDraft.rows[rowId]?.udFields;
export const selectRowActivityUdFields = (state, rowId) => state.timesheetDraft.rows[rowId]?.activityUdFields;

export default timesheetDraftSlice.reducer;
